import React, { useState, useEffect } from 'react';
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { getDateString } from '../../Components/Functions';
import { BookingType, BookingDetailsType, NamedDriverType, OptionalExtraType } from '../../Components/Types';
import { toastConfig } from '../../Components/Constants';
import Banner from '../../Components/Banner';
import { useNavigate, useParams } from 'react-router';
import { useMsal } from '@azure/msal-react';
import { toast } from 'react-toastify';
import styled from "styled-components";
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import FormsContainer from './FormsContainer';
import SummaryContainer from './SummaryContainer';
import { setCurrentEmployee } from 'features/Auth-Slice';
import { setCurrentBooking } from 'features/Booking-Slice';
import { BookingAPI } from 'API/BookingAPI';
import { NamedDriverAPI } from 'API/NamedDriverAPI';
import { EmployeeAPI } from 'API/EmployeeAPI';
import { TermsConditionsAPI } from 'API/GeneralAPI';
import { PaymentAPI } from 'API/PaymentAPI';
import { appInsights } from 'appInsightsAuth';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

const ContainerForm = styled.form`
    display: flex;
    flex-wrap: wrap;
    justify-content:center;
    padding: 0 3em 8em 3em;
    @media (min-width: 870px) {
        padding: 0 8em 8em 8em;
    }
`;
function BookingDetailsPage() {
    const [optionalExtras, setOptionalExtras] = useState<OptionalExtraType[]>([]);
    const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
    const { bookingId } = useParams<{ bookingId: string}>();
    const navigate = useNavigate();
    const methods = useForm<BookingDetailsType>();
    const { instance } = useMsal();
    const { currentEmployee  } = useSelector((state: RootState) => state.auth);
    const { currentBooking  } = useSelector((state: RootState) => state.booking);
    const dispatch = useDispatch();

    useEffect(() => {
        window.scrollTo(0, 0)
    },[])
    useEffect(() => {
        const getData = async() => {
            try {
                let booking = {} as BookingType;
                const response = await BookingAPI.getSingle(instance, parseInt(bookingId ?? ""));
                booking = response.booking;
                booking.optional_extras = response.optionalExtras;
                booking.additional_driver = response.additionalDriver;
                dispatch(setCurrentBooking(booking));
                let mainDriver = {} as NamedDriverType;
                mainDriver.dob = currentEmployee?.dob ? new Date(Date.parse(currentEmployee.dob.toString())) : null;
                mainDriver.main_driver = true;
                setOptionalExtras(currentBooking?.optional_extras ?? []);
                let additionalDriver = {...currentBooking?.additional_driver};
                if (additionalDriver.dob) {
                    additionalDriver.dob = new Date(Date.parse(additionalDriver.dob.toString()));
                }
                const formValues = { ...currentEmployee, ...mainDriver, additional_drivers:[{...additionalDriver}]};
                methods.reset(formValues);
            }
            catch (error: any){
                appInsights.trackException({ error, severityLevel: SeverityLevel.Error });
                toast.error(error, { ...toastConfig, toastId:  "booking-details-toast" });
                dispatch(setCurrentBooking(null));
                setDisableSubmit(true);
            }
            
        }
        getData();
    }, [currentEmployee]);

    const onSubmit: SubmitHandler<BookingDetailsType> = async data => {
        try {
            setDisableSubmit(true);
            const additionalDriverAdded = optionalExtras.find(oe => oe.additional_driver == true && oe.added == true) !== undefined;

            if (!data.main_driver_check && !additionalDriverAdded) {
                const message = "Sorry, you must add an additional driver";
                toast.error(message, { ...toastConfig, toastId: message + "-toast" });
                setDisableSubmit(false);
            }
            else {
                let namedDriverData = {
                    ...data,
                    employee_id: currentEmployee?.employee_id ?? 0,
                    booking_id: currentBooking?.booking_id ?? 0,
                    main_driver: true,
                    years_passed_test: data.main_driver_check ? data.years_passed_test : 0,
                    number_accidents: data.main_driver_check ? data.number_accidents : 0,
                    mandate_status: 0
                };
                namedDriverData.dob = data.dob instanceof Date ? getDateString(data.dob) : null;

                var currentEmployeeData: any = {
                    ...currentEmployee,
                    ...data,
                    dob: data.dob instanceof Date ? getDateString(data.dob) : null,
                    years_passed_test: data.main_driver_check ? data.years_passed_test : 0,
                    number_accidents: data.main_driver_check ? data.number_accidents : 0
                };
                await NamedDriverAPI.update(instance, namedDriverData);
                await EmployeeAPI.update(instance, currentEmployeeData);
                dispatch(setCurrentEmployee(currentEmployeeData));
                if (additionalDriverAdded) {
                    namedDriverData = {
                        ...namedDriverData,
                        ...data.additional_drivers[0],
                        years_passed_test: data.additional_drivers[0].years_passed_test ?? 0,
                        number_accidents: data.additional_drivers[0].number_accidents ?? 0,
                        main_driver: false,
                    }
                    namedDriverData.dob = data.additional_drivers[0].dob instanceof Date ? getDateString(data.additional_drivers[0].dob) : null;
                    await NamedDriverAPI.update(instance, namedDriverData);
                    confirmTermsConditions();
                }
                else {
                    confirmTermsConditions();
                }
            }
        }
        catch {
            setDisableSubmit(false);
        }
    }

    const confirmTermsConditions = async () => {
        await TermsConditionsAPI.confirm(instance, parseInt(bookingId ?? ""));
        if (currentBooking && currentBooking?.total_cost > 0) {
            const response = await PaymentAPI.process(instance, parseInt(bookingId ?? ""), currentBooking?.total_cost, true);
            setTimeout(() => {
                window.location.replace(response.message);
            }, 2000);
        }
        else {
            navigate(`/PaymentProcess?booking_id=${bookingId}&payment_value=${currentBooking?.total_cost}&status=new`);
        }
    }
    return (
        <>
            <Banner mainText={"Continue with booking"} />
            <FormProvider {...methods}>
                <ContainerForm onSubmit={methods.handleSubmit(onSubmit)}>
                    <FormsContainer setOptionalExtras={setOptionalExtras}/>
                    <SummaryContainer disableSubmitBtn={disableSubmit}/>
                </ContainerForm>
            </FormProvider>
        </>

    );
}
export default BookingDetailsPage;

